/**
 * Mutabakat kuyruğu — KK-805 (MVP'nin kritik ekranı), KK-412, KK-414 / AK-14.
 *
 * =============================================================================
 * NEDEN LİSTE DEĞİL, KARAR EKRANI
 * -----------------------------------------------------------------------------
 * Buradaki her satır askıda kalmış GERÇEK PARADIR. Müdür "hangi satış, kim, ne
 * kadar, hangi noktada, neden reddedildi ve NE KADARDIR BEKLİYOR" sorularının
 * hepsini AYNI KARTTA görmeden karar veremez. Bu yüzden kayıtlar tabloya
 * sıkıştırılmaz; her kayıt satış satırlarıyla birlikte açılır ve karar düğmesi
 * kaydın yanında durur.
 *
 * YAŞLANMA bilerek öne çıkarılır: 3 dakikadır bekleyen bir kayıtla 3 gündür
 * bekleyen bir kayıt aynı görünürse kuyruk yaşlanır ve KK-412'nin "satış
 * silinmez, mutabakata düşer" garantisi kâğıt üzerinde kalır.
 *
 * SEBEP 2 (çapraz limit aşımı) AYRI SÜZGEÇTİR: AK-14'te iki nokta da offline
 * iken günlük limit aşılır ve sunucu bunu ancak senkrondan SONRA görür. Bu,
 * "sistem hata yaptı" değil "offline çalışmanın bilinen bedeli"dir; aynı
 * kuyrukta ama ayrı gözle bakılması gerekir.
 *
 * ÜÇ KARARIN ANLAMI (sunucu sözleşmesi):
 *   · kabul    : satış meşrudur; durum 'tamam'a çekilir, eksikse para hareketi
 *                oluşturulur.
 *   · iptal_et : ters ledger + stok geri (KK-203). Satış SİLİNMEZ (KK-412).
 *   · not_dus  : para/stok hareketi olmaz; kayıt yalnızca açıklamasıyla kapanır.
 * =============================================================================
 */
import { useMemo, useState } from "react";
import { Buton, Kart, Rozet, Tutar, Uyari } from "@kantin/ui";
import { MutabakatSebebi, ROL_ADI, Rol } from "@kantin/shared";
import { oturumDurumu, useYetkili } from "../../veri/oturum.js";
import { useMutabakat, useMutabakatCoz } from "../../veri/sorgular.js";
import type { MutabakatKaydi, MutabakatKarari } from "../../veri/tipler.js";
import {
  ODEME_TIPI_ADI,
  SATIS_DURUM_ADI,
  SEBEP_ADI,
  gecenMs,
  kisiAdi,
  tarihSaat,
  yaslanma,
} from "../../veri/bicim.js";
import {
  FiltreSeridi,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  Sayfalayici,
  Sekmeler,
  SorguDurumu,
  YaslanmaRozeti,
} from "../../ortak.js";

type Gorunum = "bekleyen" | "cozulmus" | "tumu";

const GORUNUM_COZULDU: Record<Gorunum, boolean | null> = {
  bekleyen: false,
  cozulmus: true,
  tumu: null,
};

const SEBEP_SECENEKLERI: { deger: string; metin: string }[] = [
  { deger: "", metin: "Tüm sebepler" },
  {
    deger: String(MutabakatSebebi.CAPRAZ_LIMIT_ASIMI),
    metin: "Yalnızca çapraz limit aşımı (KK-414)",
  },
  { deger: String(MutabakatSebebi.SUNUCU_REDDI), metin: "Sunucu reddi" },
  { deger: String(MutabakatSebebi.TRANSFER_FARKI), metin: "Transfer farkı" },
  { deger: String(MutabakatSebebi.BAKIYE_SAPMASI), metin: "Bakiye sapması" },
];

export function Mutabakat() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const [gorunum, setGorunum] = useState<Gorunum>("bekleyen");
  const [sebep, setSebep] = useState("");
  const [sayfa, setSayfa] = useState(1);

  const sorgu = useMutabakat(GORUNUM_COZULDU[gorunum], sayfa);

  const kayitlar = useMemo(() => {
    const hepsi = sorgu.data?.kayitlar ?? [];
    return hepsi.filter((k) => {
      if (sebep && String(k.sebep) !== sebep) return false;
      if (seciliNoktaId && k.nokta_id !== seciliNoktaId) return false;
      return true;
    });
  }, [sorgu.data, sebep, seciliNoktaId]);

  const suzgecAcik = Boolean(sebep || seciliNoktaId);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Mutabakat kuyruğu"
        aciklama="Sunucunun reddettiği veya şüpheli işaretlediği offline satışlar. Her kayıt bir karar bekler; karar verilene kadar para askıdadır."
      />

      <Sekmeler<Gorunum>
        etiket="Mutabakat görünümü"
        secili={gorunum}
        sec={(y) => {
          setGorunum(y);
          setSayfa(1);
        }}
        sekmeler={[
          { deger: "bekleyen", metin: "Bekleyen" },
          { deger: "cozulmus", metin: "Çözülmüş (denetim izi)" },
          { deger: "tumu", metin: "Tümü" },
        ]}
      />

      <FiltreSeridi>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="mutabakat-sebep"
            className="text-sm font-semibold text-murekkep-2"
          >
            Sebep
          </label>
          <select
            id="mutabakat-sebep"
            value={sebep}
            onChange={(e) => setSebep(e.target.value)}
            className="min-h-hedef rounded-lg border border-cetvel bg-yuzey px-3 font-govde text-md text-murekkep"
          >
            {SEBEP_SECENEKLERI.map((s) => (
              <option key={s.deger} value={s.deger}>
                {s.metin}
              </option>
            ))}
          </select>
        </div>

        {suzgecAcik && (
          <p className="m-0 max-w-prose text-xs text-murekkep-3" role="status">
            Sebep ve nokta süzgeci sunucuda değil, AÇIK SAYFADA uygulanır:
            aşağıda bu sayfadaki{" "}
            <span className="rakam font-mono">
              {sorgu.data?.kayitlar.length ?? 0}
            </span>{" "}
            kayıttan <span className="rakam font-mono">{kayitlar.length}</span>{" "}
            tanesi gösteriliyor. Tam sayım için süzgeci kaldırın.
          </p>
        )}
      </FiltreSeridi>

      <SorguDurumu
        yukleniyor={sorgu.isLoading}
        hata={sorgu.error}
        bos={!sorgu.isLoading && kayitlar.length === 0}
        bosBaslik={
          gorunum === "bekleyen"
            ? "Bekleyen mutabakat yok"
            : "Bu süzgeçte kayıt yok"
        }
        bosAciklama={
          gorunum === "bekleyen"
            ? "Şu anda askıda kalmış satış yok. Kuyruk temiz."
            : "Süzgeci genişletmeyi deneyin."
        }
        iskeletSatir={6}
        yenile={() => void sorgu.refetch()}
      >
        <ul className="m-0 flex list-none flex-col gap-4 p-0">
          {kayitlar.map((k) => (
            <li key={k.id}>
              <KayitKarti kayit={k} />
            </li>
          ))}
        </ul>

        <Sayfalayici
          sayfa={sorgu.data?.sayfa ?? 1}
          boyut={sorgu.data?.sayfa_boyu ?? 50}
          toplam={sorgu.data?.toplam ?? 0}
          degistir={setSayfa}
        />
      </SorguDurumu>
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function KayitKarti({ kayit }: { kayit: MutabakatKaydi }) {
  const yas = gecenMs(kayit.olusma_zamani);
  const capraz = kayit.sebep === MutabakatSebebi.CAPRAZ_LIMIT_ASIMI;

  return (
    <Kart
      baslik={
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-lg">{kayit.nokta_ad ?? "Noktasız kayıt"}</h2>
          <Rozet tur={capraz ? "uyari" : "hata"}>
            {SEBEP_ADI[kayit.sebep] ?? `Sebep ${kayit.sebep}`}
          </Rozet>
          {kayit.hata_kodu && <Rozet tur="notr">{kayit.hata_kodu}</Rozet>}
          {kayit.cozuldu ? (
            <Rozet tur="onay">çözüldü</Rozet>
          ) : (
            <YaslanmaRozeti ms={yas} metin={yaslanma(kayit.olusma_zamani)} />
          )}
        </div>
      }
      ustBilgi={
        /* Tutar YÖNSÜZ gösterilir: mutabakat kaydındaki rakam bir defter
           satırı değil, ASKIDAKİ TUTARDIR — yönü karar verildiğinde belli
           olur (kabul edilirse ciro, iptal edilirse ters kayıt). */
        kayit.tutar ? (
          <Tutar deger={kayit.tutar} yonsuz boy="xl" etiket="askıdaki tutar" />
        ) : null
      }
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className="flex flex-col gap-2 text-sm">
          <p className="m-0">
            <span className="text-murekkep-3">Kişi:</span>{" "}
            <strong className="text-murekkep">{kisiAdi(kayit.kisi)}</strong>
          </p>
          <p className="m-0">
            <span className="text-murekkep-3">Oluşma:</span>{" "}
            <span className="rakam font-mono">
              {tarihSaat(kayit.olusma_zamani)}
            </span>
          </p>
          {kayit.satis && (
            <>
              <p className="m-0">
                <span className="text-murekkep-3">Satış:</span>{" "}
                <span className="rakam font-mono">{kayit.satis.id}</span> ·{" "}
                {tarihSaat(kayit.satis.zamani)}
              </p>
              <p className="m-0 flex flex-wrap items-center gap-2">
                {kayit.satis.durum !== null && (
                  <Rozet tur="notr">
                    {SATIS_DURUM_ADI[kayit.satis.durum] ??
                      `Durum ${kayit.satis.durum}`}
                  </Rozet>
                )}
                {kayit.satis.odeme_tipi !== null && (
                  <Rozet tur="notr">
                    {ODEME_TIPI_ADI[kayit.satis.odeme_tipi] ?? "Ödeme"}
                  </Rozet>
                )}
                {kayit.satis.offline_mi && <Rozet tur="uyari">offline</Rozet>}
                {kayit.satis.terminal_id !== null && (
                  <span className="text-murekkep-3">
                    terminal{" "}
                    <span className="rakam font-mono">
                      {kayit.satis.terminal_id}
                    </span>
                  </span>
                )}
              </p>
            </>
          )}
          {kayit.transfer_id && (
            <p className="m-0">
              <span className="text-murekkep-3">Transfer:</span>{" "}
              <span className="rakam font-mono">{kayit.transfer_id}</span>
            </p>
          )}

          <Uyari tur={capraz ? "uyari" : "bilgi"} baslik="Açıklama">
            {kayit.aciklama}
            {capraz && (
              <p className="m-0 mt-1">
                İki nokta da offline iken günlük limit aşılmış olabilir (KK-414
                / AK-14). Bu, offline çalışmanın bilinen bedelidir; karar
                verirken öğrencinin toplam gün harcamasına bakın.
              </p>
            )}
          </Uyari>
        </div>

        <SatisSatirlari satirlar={kayit.satis?.satirlar ?? []} />
      </div>

      {kayit.cozuldu ? (
        <div className="mt-4 rounded-md border border-cetvel bg-kagit-2 p-3 text-sm">
          <p className="m-0">
            <span className="text-murekkep-3">Çözüm:</span>{" "}
            <span className="rakam font-mono">
              {tarihSaat(kayit.cozum_zamani)}
            </span>
            {kayit.cozen_id && (
              <>
                {" "}
                · personel{" "}
                <span className="rakam font-mono">{kayit.cozen_id}</span>
              </>
            )}
          </p>
          {kayit.cozum_notu && (
            <p className="m-0 mt-1 text-murekkep">{kayit.cozum_notu}</p>
          )}
        </div>
      ) : (
        <KararFormu kayit={kayit} />
      )}
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function SatisSatirlari({
  satirlar,
}: {
  satirlar: {
    urun_id: string;
    urun_ad: string;
    adet: string;
    birim_fiyat: string;
    satir_toplam: string;
  }[];
}) {
  if (satirlar.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-cetvel-koyu p-3 text-sm text-murekkep-3">
        Bu kayda bağlı satış satırı yok (transfer veya bakiye kaynaklı kayıt).
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-cetvel">
      <table className="w-full border-collapse text-sm">
        <caption className="border-b border-cetvel bg-kagit-2 px-3 py-2 text-left text-xs text-murekkep-2">
          Satış satırları
        </caption>
        <thead className="bg-kagit-2">
          <tr>
            <th
              scope="col"
              className="border-b border-cetvel-silik px-3 py-2 text-left text-xs text-murekkep-2"
            >
              Ürün
            </th>
            <th
              scope="col"
              className="border-b border-cetvel-silik px-3 py-2 text-right text-xs text-murekkep-2"
            >
              Adet
            </th>
            <th
              scope="col"
              className="border-b border-cetvel-silik px-3 py-2 text-right text-xs text-murekkep-2"
            >
              Birim
            </th>
            <th
              scope="col"
              className="border-b border-cetvel-silik px-3 py-2 text-right text-xs text-murekkep-2"
            >
              Tutar
            </th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr
              key={s.urun_id}
              className="border-b border-cetvel last:border-0"
            >
              <td className="px-3 py-1.5">{s.urun_ad}</td>
              <td className="rakam px-3 py-1.5 text-right font-mono">
                {s.adet}
              </td>
              <td className="px-3 py-1.5 text-right">
                <Tutar deger={s.birim_fiyat} yonsuz boy="sm" />
              </td>
              <td className="px-3 py-1.5 text-right">
                <Tutar deger={s.satir_toplam} yonsuz boy="sm" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

function KararFormu({ kayit }: { kayit: MutabakatKaydi }) {
  /**
   * §5.1: "iptal_et" kararı bir SATIŞ İPTALİ üretir ve o satırda okul yönetimi
   * yetkisizdir; bu yüzden sunucu çözüm ucunu kantin müdürü ve okul sistem yöneticisine
   * açar. Kuyruğu GÖRMEK herkese açıktır — karar formu gizlenir, kayıt değil.
   */
  const cozebilir = useYetkili(Rol.KANTIN_MUDURU, Rol.OKUL_SISTEM_YONETICISI);
  const [karar, setKarar] = useState<MutabakatKarari | "">("");
  const [not, setNot] = useState("");
  const coz = useMutabakatCoz();

  const uygula = () => {
    if (!karar) return;
    coz.mutate({
      id: kayit.id,
      karar,
      ...(not.trim() ? { not: not.trim() } : {}),
    });
  };

  const kararMetni: Record<MutabakatKarari, string> = {
    kabul:
      "Satış meşru sayılır; durum 'tamam'a çekilir, eksik para hareketi oluşturulur.",
    iptal_et:
      "Ters kayıt yazılır ve stok geri alınır (KK-203). Satış silinmez, iptal olarak kalır (KK-412).",
    not_dus:
      "Hiçbir para veya stok hareketi oluşmaz; kayıt yalnızca açıklamasıyla kapanır.",
  };

  if (!cozebilir) {
    return (
      <div className="mt-4 border-t border-cetvel pt-3">
        <Uyari tur="bilgi" baslik="Bu kaydı siz çözemezsiniz">
          Kararlardan biri (&quot;İptal et&quot;) satış iptali üretir;
          §5.1&apos;de bu yetki <strong>{ROL_ADI[Rol.KANTIN_MUDURU]}</strong> ve
          okul sistem yöneticisindedir. Kayıt burada durur ve yaşlanır — çözülmesi
          için kantin müdürüne iletin.
        </Uyari>
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-cetvel pt-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`karar-${kayit.id}`}
            className="text-sm font-semibold text-murekkep-2"
          >
            Karar
          </label>
          <select
            id={`karar-${kayit.id}`}
            value={karar}
            onChange={(e) => setKarar(e.target.value as MutabakatKarari | "")}
            className="min-h-hedef rounded-lg border border-cetvel bg-yuzey px-3 font-govde text-md text-murekkep"
          >
            <option value="">Karar seçin</option>
            <option value="kabul">Kabul et</option>
            <option value="iptal_et">İptal et</option>
            <option value="not_dus">Not düş (kapat)</option>
          </select>
        </div>

        <div className="min-w-[16rem] flex-1">
          <MetinAlani
            etiket="Not (denetime yazılır)"
            rows={2}
            value={not}
            onChange={(e) => setNot(e.target.value)}
            ipucu="Karar gerekçesi audit log'a işlenir; sonradan değiştirilemez."
          />
        </div>

        <Buton
          tur={karar === "iptal_et" ? "tehlike" : "onay"}
          disabled={!karar}
          yukleniyor={coz.isPending}
          onClick={uygula}
        >
          Kararı uygula
        </Buton>
      </div>

      {karar && (
        <p
          className="m-0 mt-2 max-w-prose text-xs text-murekkep-3"
          role="status"
        >
          {kararMetni[karar]}
        </p>
      )}

      <div className="mt-2">
        <IslemDurumu
          hata={coz.error}
          basari={coz.isSuccess ? "Karar kaydedildi." : null}
        />
      </div>
    </div>
  );
}
